import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { DropdownItem } from "@/modules/dropdowns/types";
import type {
  CreateCollectionPayload,
  CreateExternalLinkPayload,
  MediaCollection,
  MediaComment,
  MediaFile,
  MediaFileDetail,
  MediaListParams,
  MediaListResponse,
  MediaStats,
  MediaTreeParams,
  MediaTreeResponse,
  UpdateCollectionPayload,
  UpdateMediaPayload,
  UploadFileInput,
} from "../types";

const MEDIA_KEY = ["media"] as const;
const COLLECTIONS_KEY = ["media", "collections"] as const;
const STATS_KEY = ["media", "stats"] as const;

const API_BASE = (
  import.meta.env.VITE_API_URL ?? 
  (import.meta.env.DEV ? "http://localhost:3000/api" : "/api")
).replace(/\/$/, "");

export function useMediaList(
  params: MediaListParams = {},
): UseQueryResult<MediaListResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...MEDIA_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<MediaListResponse>("/media", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          fileType: params.fileType,
          tags: params.tags && params.tags.length > 0
            ? JSON.stringify(params.tags)
            : undefined,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          meetingId: params.meetingId,
          taskId: params.taskId,
          actionId: params.actionId,
          noteId: params.noteId,
          milestoneId: params.milestoneId,
          budgetId: params.budgetId,
          expenseId: params.expenseId,
          entityType: params.entityType,
          entityId: params.entityId,
          includeChildren: params.includeChildren ? "true" : undefined,
          phaseId: params.phaseId,
          stageId: params.stageId,
          collectionId: params.collectionId,
          uploadedById: params.uploadedById,
          myFiles: params.myFiles ? "true" : undefined,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          sortBy: params.sortBy,
        },
      }),
  });
}

const TREE_KEY = ["media", "tree"] as const;

export function useMediaTree(
  params: MediaTreeParams = {},
): UseQueryResult<MediaTreeResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...TREE_KEY, params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<MediaTreeResponse>("/media/tree", {
        token: accessToken,
        query: {
          rootEntityType: params.rootEntityType,
          rootEntityId: params.rootEntityId,
          search: params.search,
          fileType: params.fileType,
          tags: params.tags && params.tags.length > 0
            ? JSON.stringify(params.tags)
            : undefined,
          uploadedById: params.uploadedById,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          perNodeLimit: params.perNodeLimit,
        },
      }),
  });
}

export function useMediaFile(id: string): UseQueryResult<MediaFileDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...MEDIA_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ file: MediaFileDetail }>(`/media/${id}`, {
        token: accessToken,
      });
      return res.file;
    },
  });
}

export function useMediaStats(): UseQueryResult<MediaStats> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...STATS_KEY, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<MediaStats>("/media/stats", { token: accessToken }),
  });
}

export function useMediaCollections(): UseQueryResult<MediaCollection[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...COLLECTIONS_KEY, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ collections: MediaCollection[] }>(
        "/media/collections",
        { token: accessToken },
      );
      return res.collections;
    },
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: MEDIA_KEY });
  qc.invalidateQueries({ queryKey: STATS_KEY });
  qc.invalidateQueries({ queryKey: COLLECTIONS_KEY });
  qc.invalidateQueries({ queryKey: TREE_KEY });
}

export function useUploadFile() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  function mutate(input: UploadFileInput): Promise<MediaFileDetail> {
    setProgress(0);
    setIsUploading(true);
    setError(null);

    return new Promise<MediaFileDetail>((resolve, reject) => {
      const fd = new FormData();
      fd.append("file", input.file);
      if (input.title) fd.append("title", input.title);
      if (input.description) fd.append("description", input.description);
      if (input.tags && input.tags.length > 0)
        fd.append("tags", JSON.stringify(input.tags));
      if (input.visibility) fd.append("visibility", input.visibility);
      if (input.collectionId) fd.append("collectionId", input.collectionId);
      if (input.entityType) fd.append("entityType", input.entityType);
      if (input.entityId) fd.append("entityId", input.entityId);
      if (input.entityLabel) fd.append("entityLabel", input.entityLabel);
      if (input.phaseId) fd.append("phaseId", input.phaseId);
      if (input.stageId) fd.append("stageId", input.stageId);
      if (input.companyId) fd.append("companyId", input.companyId);
      if (input.productId) fd.append("productId", input.productId);
      if (input.projectId) fd.append("projectId", input.projectId);
      if (input.companyIds && input.companyIds.length > 0)
        fd.append("companyIds", JSON.stringify(input.companyIds));
      if (input.productIds && input.productIds.length > 0)
        fd.append("productIds", JSON.stringify(input.productIds));
      if (input.projectIds && input.projectIds.length > 0)
        fd.append("projectIds", JSON.stringify(input.projectIds));
      if (input.meetingId) fd.append("meetingId", input.meetingId);
      if (input.taskId) fd.append("taskId", input.taskId);
      if (input.actionId) fd.append("actionId", input.actionId);
      if (input.noteId) fd.append("noteId", input.noteId);
      if (input.milestoneId) fd.append("milestoneId", input.milestoneId);
      if (input.budgetId) fd.append("budgetId", input.budgetId);
      if (input.expenseId) fd.append("expenseId", input.expenseId);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE}/media/upload`);
      if (accessToken) xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
      xhr.withCredentials = true;
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      xhr.addEventListener("load", () => {
        setIsUploading(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const payload = JSON.parse(xhr.responseText) as {
              file: MediaFileDetail;
            };
            invalidateAll(qc);
            resolve(payload.file);
          } catch (e) {
            reject(e);
          }
        } else {
          let message = `Upload failed (${xhr.status})`;
          try {
            const payload = JSON.parse(xhr.responseText);
            if (payload?.error) message = String(payload.error);
          } catch {
            /* ignore */
          }
          const e = new Error(message);
          setError(e);
          reject(e);
        }
      });
      xhr.addEventListener("error", () => {
        setIsUploading(false);
        const e = new Error("Upload failed");
        setError(e);
        reject(e);
      });
      xhr.send(fd);
    });
  }

  return { mutate, progress, isUploading, error };
}

export function useUploadVersion(fileId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return new Promise<MediaFileDetail>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_BASE}/media/${fileId}/version`);
        if (accessToken)
          xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
        xhr.withCredentials = true;
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const payload = JSON.parse(xhr.responseText) as {
                file: MediaFileDetail;
              };
              resolve(payload.file);
            } catch (e) {
              reject(e);
            }
          } else {
            reject(new Error(`Version upload failed (${xhr.status})`));
          }
        });
        xhr.addEventListener("error", () => reject(new Error("Upload failed")));
        xhr.send(fd);
      });
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddExternalLink() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateExternalLinkPayload) =>
      apiFetch<{ file: MediaFileDetail }>("/media/link", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export type LinkExistingTargetType =
  | "company"
  | "product"
  | "project"
  | "meeting"
  | "task"
  | "action"
  | "note"
  | "milestone"
  | "budget"
  | "expense"
  | "idea"
  | "decision"
  | "asset"
  | "lead"
  | "enquiry";

export type LinkExistingPayload = {
  fileIds: string[];
  target: {
    type: LinkExistingTargetType;
    id: string;
    label?: string | null;
  };
};

export function useLinkExistingMedia() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LinkExistingPayload) =>
      apiFetch<{ files: MediaFileDetail[] }>("/media/link-existing", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateMediaFile(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateMediaPayload) =>
      apiFetch<{ file: MediaFileDetail }>(`/media/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteMediaFile() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/media/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useBulkDeleteMedia() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(
        ids.map((id) =>
          apiFetch(`/media/${id}`, { method: "DELETE", token: accessToken }),
        ),
      );
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useMoveToCollection() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      collectionId,
    }: {
      ids: string[];
      collectionId: string | null;
    }) => {
      await Promise.all(
        ids.map((id) =>
          apiFetch(`/media/${id}`, {
            method: "PUT",
            body: { collectionId },
            token: accessToken,
          }),
        ),
      );
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDownloadFile() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: Pick<MediaFile, "id" | "originalName" | "isExternal" | "externalUrl">) => {
      if (file.isExternal && file.externalUrl) {
        window.open(file.externalUrl, "_blank", "noopener,noreferrer");
        return;
      }
      // Fetch the signed URL from our same-origin JSON endpoint (Bearer auth,
      // credentials are fine here). We must NOT fetch the file itself: the
      // /media/:id/download endpoint 302-redirects to a cross-origin R2 signed
      // URL, and fetch() with credentials:"include" fails CORS because R2 does
      // not return Access-Control-Allow-Credentials (by design — signed URLs
      // are self-authenticating). Instead, hand the signed URL to the browser
      // via an anchor click so it navigates natively: no credentials are sent,
      // so there is no CORS check. R2's ResponseContentDisposition preserves
      // the original filename.
      const { url } = await apiFetch<{ url: string }>(
        `/media/${file.id}/download-url`,
        { token: accessToken },
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = file.originalName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCreateCollection() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCollectionPayload) =>
      apiFetch<{ collection: MediaCollection }>("/media/collections", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COLLECTIONS_KEY }),
  });
}

export function useUpdateCollection(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateCollectionPayload) =>
      apiFetch<{ collection: MediaCollection }>(`/media/collections/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COLLECTIONS_KEY }),
  });
}

export function useDeleteCollection() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/media/collections/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useMediaComments(fileId: string): UseQueryResult<MediaComment[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...MEDIA_KEY, fileId, "comments", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!fileId,
    queryFn: async () => {
      const res = await apiFetch<{ data: MediaComment[] }>(
        `/media/${fileId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddMediaComment(fileId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: MediaComment }>(`/media/${fileId}/comments`, {
        method: "POST",
        body: { content },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...MEDIA_KEY, fileId, "comments"],
      });
    },
  });
}

export function useDeleteMediaComment(fileId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/media/${fileId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...MEDIA_KEY, fileId, "comments"],
      });
    },
  });
}

export function useMediaTags(): UseQueryResult<DropdownItem[]> {
  return useCategoryItems("media_tag");
}

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function resolveFileUrl(filePath: string): string {
  if (!filePath) return "";
  if (/^https?:\/\//i.test(filePath)) return filePath;
  const origin = API_BASE.replace(/\/api$/, "");
  const normalized = filePath.replace(/^\/+/, "");
  return `${origin}/${normalized}`;
}
